import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, RefreshCw, AlertTriangle } from 'lucide-react';

export const meta = {
  id: 'exchange_rates',
  category: 'business',
  titleKey: 'dashboard.widgets.exchangeRates',
  descriptionKey: 'dashboard.widgets.exchangeRatesDesc',
  defaultSize: 'sm',
  icon: DollarSign,
};

// USD/ARS rates from dolarapi.com (public, CORS-enabled). Shown: oficial,
// blue, cripto. Refreshes every 5 min. Pure client-side fetch — CSP allows
// https: in connect-src. Fails gracefully (shows a retry hint) if offline.
const REFRESH_MS = 5 * 60 * 1000;
const WANTED = [
  { key: 'oficial', labelKey: 'dashboard.widgets.exOficial', color: '#3b82f6' },
  { key: 'blue', labelKey: 'dashboard.widgets.exBlue', color: '#22c55e' },
  { key: 'cripto', labelKey: 'dashboard.widgets.exCripto', color: '#f59e0b' },
];

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 });
}

export default function ExchangeRatesWidget({ fullscreen }) {
  const { t } = useTranslation();
  const [rates, setRates] = useState(null); // { oficial: {...}, blue: {...}, cripto: {...} }
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch('https://dolarapi.com/v1/dolares', { headers: { Accept: 'application/json' } });
      if (!resp.ok) throw new Error('bad status');
      const data = await resp.json();
      const map = {};
      for (const r of data) map[r.casa] = r;
      setRates(map);
      setErr(false);
      setUpdatedAt(Date.now());
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const tsLabel = updatedAt ? new Date(updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DollarSign size={16} /> {t('dashboard.widgets.exchangeRates')}
        </div>
        <button className="btn btn-ghost btn-icon btn-sm" title={t('common.refresh', 'Refrescar')} onClick={load}>
          <RefreshCw size={13} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
        </button>
      </div>

      <div style={{ padding: '8px 16px 14px', flex: 1 }}>
        {err && !rates ? (
          <div style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: '16px 0' }}>
            <AlertTriangle size={20} style={{ color: '#f59e0b', marginBottom: 6 }} />
            <div style={{ fontSize: 12 }}>{t('dashboard.widgets.exError')}</div>
          </div>
        ) : !rates ? (
          <div style={{ fontSize: 13, color: 'var(--c-text-muted)', padding: '8px 0' }}>{t('common.loading', 'Cargando…')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {WANTED.map(({ key, labelKey, color }) => {
              const r = rates[key];
              if (!r) return null;
              const spread = r.venta && r.compra ? r.venta - r.compra : null;
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{t(labelKey)}</div>
                    {fullscreen && spread != null && (
                      <div style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>
                        {t('dashboard.widgets.exBuy')} ${fmt(r.compra)} · {t('dashboard.widgets.exSell')} ${fmt(r.venta)}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    ${fmt(r.venta)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {tsLabel && (
        <div style={{
          padding: '6px 16px', borderTop: '1px solid var(--c-border)',
          fontSize: 10, color: 'var(--c-text-muted)', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{t('dashboard.widgets.exUpdated')} {tsLabel}</span>
          <span style={{ opacity: 0.7 }}>dolarapi.com</span>
        </div>
      )}
    </div>
  );
}
